/**
 * 📦 DPD / Pickup-Anbindung (Frontend-Service)
 *
 * Verbindet PaketPilot direkt mit der Pickup-Schnittstelle (`com.ams.pudo` / GeoPost):
 *   1. Vorab-Namen (`SHIPPED` + `DELIVERED`): Empfängernamen sind schon vor dem Fahrer-Scan bekannt!
 *   2. Foto-Station & Handy: Beim Scannen eines 14-stelligen DPD-Barcodes (oder 28-stelligen DPD-Leitcodes)
 *      wird der Name sofort zugeordnet — und bei eingeschalteter Live-Annahme auch direkt in Pickup gebucht.
 *   3. Einstellungen (`DpdPanel`): 2-Schritt-Anmeldung per Shop-ID (z. B. NL24808) + E-Mail-Einmalcode,
 *      Tagesübersicht (Im Shop / Unterwegs / Rückgabe mit Fach), Bestands-Abgleich und Protokoll.
 */
import { apiFetch } from './apiService';
import { hasCustomFeature } from './customFeatures';

const SCHALTER_KEY = 'kiosk_dpd_enabled';
const ANNAHME_LIVE_KEY = 'kiosk_dpd_live_annahme';
const AUSGABE_LIVE_KEY = 'kiosk_dpd_live_ausgabe';

// ── Freischaltung & Schalter ─────────────────────────────────────────────────

export function dpdFreigeschaltet() {
  return hasCustomFeature('dpd');
}

export function isDpdOn() {
  try {
    return localStorage.getItem(SCHALTER_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDpdOn(on) {
  try {
    localStorage.setItem(SCHALTER_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
}

export function isDpdAnnahmeOn() {
  try {
    return localStorage.getItem(ANNAHME_LIVE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDpdAnnahmeOn(on) {
  try {
    localStorage.setItem(ANNAHME_LIVE_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
  dpdNotiz('live_schalter', 'annahme', true, '', on ? 'Live-Annahme eingeschaltet' : 'Live-Annahme ausgeschaltet');
}

export function isDpdAusgabeOn() {
  try {
    return localStorage.getItem(AUSGABE_LIVE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDpdAusgabeOn(on) {
  try {
    localStorage.setItem(AUSGABE_LIVE_KEY, on ? '1' : '0');
  } catch { /* ignore */ }
  dpdNotiz('live_schalter', 'ausgabe', true, '', on ? 'Live-Ausgabe eingeschaltet' : 'Live-Ausgabe ausgeschaltet');
}

export function dpdAktiv() {
  return dpdFreigeschaltet() && isDpdOn();
}

export function dpdAnnahmeAktiv() {
  return dpdAktiv() && isDpdAnnahmeOn();
}

export function dpdAusgabeAktiv() {
  return dpdAktiv() && isDpdAusgabeOn();
}

// ── Barcode-Erkennung (14-stellige DPD-Paketnummer, 28-stelliger Leitcode & DPD-Aztec) ──

/**
 * Rekonstruiert aus einem DPD-2D-Aztec-Code („[)>\x1e01\x1d02\x1d3045RD\x1d528\x1d335\x1d05222761899169\x1d…")
 * den 28-stelligen 1D-Leitcode („%03045RD05222761899169335280").
 */
export function dpdBarcodeAusAztec(text) {
  const s = String(text || '').trim().replace(/^(\][A-Za-z]\d)+/, '').replace(/␞/g, '\x1e').replace(/␝/g, '\x1d');
  if (!s.startsWith('[)>')) return '';
  const parts = s.split('\x1d');
  if (parts.length >= 6) {
    const plz = String(parts[2] || '').trim().toUpperCase().replace(/\s+/g, '');
    const land = String(parts[3] || '').trim();
    const service = String(parts[4] || '').trim();
    const parcel = String(parts[5] || '').trim();
    if (/^[0-9A-Z]{4,7}$/.test(plz) && /^\d{3}$/.test(land) && /^\d{3}$/.test(service) && /^\d{14}$/.test(parcel)) {
      return '%' + plz.padStart(7, '0') + parcel + service + land;
    }
  }
  const m14 = s.match(/(?:^|[\x1d\x1e|;\s])(\d{14})(?=$|[\x1d\x1e|;\s])/);
  return m14 ? m14[1] : '';
}

/**
 * Extrahiert die 14-stellige DPD-Paketnummer aus einem Barcode oder Tracking-String.
 * - Klassischer DPD-Barcode: genau 14 Ziffern (z. B. `05222761899169`)
 * - Langer DPD-128-Leitcode (27–28 Zeichen nach `%`, in NL mit Buchstaben in der PLZ wie `%03045RD05222761899169335280`):
 *   Zeichen 8..21 (0-basiert 7..21) sind die 14-stellige Paketnummer.
 * - DPD-2D-Aztec-Code (`[)>\x1e01...`): Feld 5 ist die 14-stellige Paketnummer.
 */
export function dpdCodeAusTracking(trk) {
  const raw = String(trk || '').trim();
  if (!raw) return '';
  const aztec = dpdBarcodeAusAztec(raw);
  if (aztec) {
    return /^\d{14}$/.test(aztec) ? aztec : aztec.replace(/^%+/, '').slice(7, 21);
  }
  const clean = raw.replace(/^(\][A-Za-z]\d)+/, '').replace(/^%+/, '').replace(/[\s-]+/g, '').toUpperCase();
  if (/^\d{14}$/.test(clean)) return clean;
  // NL-PLZ am Anfang (z. B. 03045RD oder 003045RD direkt vor der 14-stelligen Paketnummer)
  const mNl = /^\d{4,6}[A-Z]{2}(\d{14})\d{5,6}[0-9A-Z]?$/.exec(clean);
  if (mNl) return mNl[1];
  // Allgemeiner 27/28-stelliger DPD-Leitcode (7 Zeichen PLZ + 14 Ziffern Paketnummer + 6–7 Zeichen Service/Land/Prüfziffer)
  const m28 = /^[0-9A-Z]{7}(\d{14})\d{5,6}[0-9A-Z]$/.exec(clean);
  if (m28) return m28[1];
  // Fallback: Falls Vorab-Namen geladen sind und ein längerer Scan-Code exakt eine bekannte 14-stellige DPD-Nummer enthält
  if (bekannteNamen.size > 0 && clean.length > 14 && !/^(3S|JVGL|JJD|1Z|VGS)/i.test(clean)) {
    for (let i = 0; i <= clean.length - 14; i++) {
      const sub = clean.slice(i, i + 14);
      if (/^\d{14}$/.test(sub) && bekannteNamen.has(sub)) return sub;
    }
  }
  return '';
}

/**
 * Liefert den bereinigten Barcode für die Übergabe an DPD Pickup (`event/v3`):
 * Wandelt einen 2D-Aztec-Code in den 28-stelligen `%...`-Leitcode um.
 */
export function dpdScanBarcode(trk) {
  const raw = String(trk || '').trim();
  if (!raw) return '';
  const aztec = dpdBarcodeAusAztec(raw);
  if (aztec) return aztec;
  const clean = raw.replace(/^(\][A-Za-z]\d)+/, '').replace(/[\s-]+/g, '').toUpperCase();
  const oP = clean.replace(/^%+/, '');
  if (/^[0-9A-Z]{7}\d{14}\d{5,6}[0-9A-Z]$/.test(oP)) return '%' + oP;
  return dpdCodeAusTracking(raw) || clean;
}

export function istDpdLangBarcode(trk) {
  const raw = String(trk || '').trim();
  if (!raw) return false;
  if (dpdBarcodeAusAztec(raw)) return true;
  const clean = raw.replace(/^(\][A-Za-z]\d)+/, '').replace(/^%+/, '').replace(/[\s-]+/g, '').toUpperCase();
  return /^[0-9A-Z]{7}\d{14}\d{5,6}[0-9A-Z]$/.test(clean) || /^\d{4,6}[A-Z]{2}\d{14}\d{5,6}[0-9A-Z]?$/.test(clean);
}

export function istDpdScanCode(trk) {
  return !!dpdCodeAusTracking(trk);
}

export function dpdKurz(trk) {
  const c = dpdCodeAusTracking(trk) || String(trk || '').replace(/\D/g, '');
  return c.slice(-6);
}

export function dpdPlatzhalterName(trk) {
  const k = dpdKurz(trk);
  return k ? `DPD ${k}` : 'DPD';
}

const FREMDE_DIENSTE = new Set(['vinted', 'vinted go', 'dhl', 'gls', 'ups', 'hermes', 'mondial', 'mondial relay', 'postnl', 'bpost', 'amazon']);

/**
 * Prüft, ob ein Paket in PaketPilot ein DPD-Paket ist.
 */
export function istDpdPaket(p) {
  if (!p) return false;
  const dienst = String(p.paketdienst || p.dienst || p.carrier || '').trim().toLowerCase();
  if (dienst === 'dpd' || dienst === 'pickup' || dienst === 'chronopost') return true;
  if (dienst && FREMDE_DIENSTE.has(dienst)) return false;
  const rawTrk = String(p.trackingNumber || p.tracking || p.sendungsnummer || p.code || '').trim();
  const code = dpdCodeAusTracking(rawTrk);
  if (code && (bekannterDpdName(code) || istDpdLangBarcode(rawTrk))) return true;
  const hinweis = String(p.hinweis || p.notiz || p.note || '').toLowerCase();
  return hinweis.includes('dpd');
}

// ── Vorab geladene Namen (0 ms Nachschlagen beim Scannen) ────────────────────

const bekannteNamen = new Map(); // 14-stellige Nummer -> Empfängername
let letztesVorladenMs = 0;
let vorladenVersprechen = null;

export function merkeDpdName(codeOrTracking, name) {
  const n = String(name || '').trim();
  if (!n) return;
  const norm = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!norm) return;
  bekannteNamen.set(norm, n);
}

export function bekannterDpdName(codeOrTracking) {
  const norm = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!norm) return '';
  if (bekannteNamen.has(norm)) return bekannteNamen.get(norm) || '';
  // Falls nur die letzten Ziffern übergeben wurden
  if (norm.length >= 6 && norm.length < 14) {
    for (const [k, v] of bekannteNamen.entries()) {
      if (k.endsWith(norm)) return v;
    }
  }
  return '';
}

export function anzahlBekannteDpdNamen() {
  return bekannteNamen.size;
}

/**
 * Lädt alle aktuellen + angekündigten DPD-Pakete (`DELIVERED` + `SHIPPED`) vom Server
 * und legt deren Empfängernamen im Speicher ab.
 */
export async function vorladenStart(force = false) {
  if (!dpdAktiv()) return { ok: false, skipped: true };
  const jetzt = Date.now();
  if (!force && jetzt - letztesVorladenMs < 25000 && bekannteNamen.size > 0) {
    return { ok: true, cached: true, count: bekannteNamen.size };
  }
  if (vorladenVersprechen) return vorladenVersprechen;

  vorladenVersprechen = (async () => {
    try {
      const r = await api('/api/dpd/vorab-namen', { method: 'GET', timeoutMs: 12000 });
      if (r && r.ok && r.namen && typeof r.namen === 'object') {
        letztesVorladenMs = Date.now();
        for (const [nr, info] of Object.entries(r.namen)) {
          const name = typeof info === 'string' ? info : (info && info.name);
          if (name) merkeDpdName(nr, name);
        }
        return { ok: true, count: bekannteNamen.size, anzahl: r.anzahl || Object.keys(r.namen).length };
      }
      return { ok: false, reason: (r && r.reason) || 'fehler', message: (r && r.message) || '' };
    } catch (e) {
      return { ok: false, reason: 'netz', message: e.message };
    } finally {
      vorladenVersprechen = null;
    }
  })();

  return vorladenVersprechen;
}

// ── HTTP-Helfer (über apiFetch mit automatischem JWT-Refresh) ────────────────

async function api(pfad, { method = 'GET', body, timeoutMs = 20000 } = {}) {
  const cleanPath = pfad.replace(/^\/api/, '');
  try {
    return await apiFetch(cleanPath, {
      method,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      timeout: timeoutMs,
    });
  } catch (e) {
    if (e && e.status === 403) return { ok: false, reason: 'nicht_freigeschaltet', message: '' };
    return {
      ok: false,
      reason: e && e.zeitueberschreitung ? 'timeout' : 'offline',
      message: (e && e.message) || 'Netzwerkfehler',
      status: e && e.status,
      netzweg: !!(e && e.netzweg),
      zeitueberschreitung: !!(e && e.zeitueberschreitung),
    };
  }
}

// ── API-Aufrufe ──────────────────────────────────────────────────────────────

export function dpdStatus() {
  return api('/api/dpd/status', { method: 'GET', timeoutMs: 8000 });
}

export async function dpdUebersicht() {
  const r = await api('/api/dpd/uebersicht', { method: 'GET', timeoutMs: 15000 });
  // Wenn die Übersicht geladen wird, füllen wir nebenbei auch das Vorab-Namensgedächtnis
  vorladenStart(false).catch(() => {});
  return r;
}

export async function dpdBestand() {
  const r = await api('/api/dpd/bestand', { method: 'GET', timeoutMs: 15000 });
  if (r && r.ok && Array.isArray(r.pakete)) {
    for (const p of r.pakete) {
      const nr = p.code || p.parcelNumber;
      const nm = p.name || p.customerName;
      if (nr && nm) merkeDpdName(nr, nm);
    }
  }
  return r;
}

export function dpdRequestCode(pudoId) {
  return api('/api/dpd/request-code', {
    method: 'POST',
    body: { pudoId: String(pudoId || '').trim().toUpperCase() },
    timeoutMs: 15000,
  });
}

export function dpdVerifyCode(pudoId, code) {
  return api('/api/dpd/verify-code', {
    method: 'POST',
    body: {
      pudoId: String(pudoId || '').trim().toUpperCase(),
      code: String(code || '').trim().toUpperCase(),
    },
    timeoutMs: 25000,
  });
}

export async function dpdLogout() {
  bekannteNamen.clear();
  letztesVorladenMs = 0;
  return api('/api/dpd/logout', { method: 'POST', body: {}, timeoutMs: 10000 });
}

export async function dpdLookup(codeOrTracking) {
  const code = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!code) return { ok: false, reason: 'eingabe_fehlt' };
  const r = await api('/api/dpd/lookup', { method: 'POST', body: { code }, timeoutMs: 12000 });
  if (r && r.ok && r.found && r.name) {
    merkeDpdName(r.code || code, r.name);
  }
  return r;
}

export async function dpdAnnahme(codeOrTracking) {
  const raw = String(codeOrTracking || '').trim();
  const norm = dpdCodeAusTracking(raw) || raw;
  if (!norm) return { ok: false, reason: 'eingabe_fehlt' };
  const r = await api('/api/dpd/intake', { method: 'POST', body: { code: raw || norm }, timeoutMs: 15000 });
  if (r && r.name) {
    merkeDpdName(r.code || norm, r.name);
  }
  return r;
}

export function dpdAusgabe(codeOrTracking, { pin = '', pickerName = '', signature = '' } = {}) {
  const code = dpdCodeAusTracking(codeOrTracking) || String(codeOrTracking || '').trim();
  if (!code) return Promise.resolve({ ok: false, reason: 'eingabe_fehlt' });
  return api('/api/dpd/pickup', {
    method: 'POST',
    body: { code, pin, pickerName, signature },
    timeoutMs: 15000,
  });
}

export function dpdAbgleich(pakete) {
  return api('/api/dpd/abgleich', {
    method: 'POST',
    body: { pakete: Array.isArray(pakete) ? pakete : [] },
    timeoutMs: 20000,
  });
}

export function dpdNotiz(op, code = '', ok = true, reason = '', message = '', detail = null) {
  if (!dpdFreigeschaltet()) return Promise.resolve({ ok: false });
  return api('/api/dpd/note', {
    method: 'POST',
    body: { op, code: String(code || '').slice(0, 40), ok: !!ok, reason, message, detail },
    timeoutMs: 5000,
  }).catch(() => ({ ok: false }));
}

export function dpdLog(limit = 80) {
  return api(`/api/dpd/log?limit=${ encodeURIComponent(limit) }`, { method: 'GET', timeoutMs: 10000 });
}

// ── Hilfsfunktionen für Abgleich & Rückgabe-Fächer ───────────────────────────

/**
 * Filtert aus allen PaketPilot-Paketen diejenigen heraus, die aktuell offen (nicht abgeholt)
 * und DPD zuzuordnen sind.
 */
export function offeneDpdPakete(packages = []) {
  const out = [];
  for (const p of packages || []) {
    if (!p || p.deleted || p.ausgegebenAm || p.zurueckgeschickt || p.abgeholt || p.pickedUp || p.status === 'picked_up') continue;
    if (!istDpdPaket(p)) continue;
    const trk = p.trackingNumber || p.tracking || p.sendungsnummer || p.code || '';
    const code = dpdCodeAusTracking(trk) || String(trk || '').trim();
    if (!code) continue;
    const name = String(p.name || p.empfaenger || `${p.vorname || ''} ${p.nachname || ''}`).trim();
    out.push({
      id: p.id,
      code,
      name,
      fach: p.regalplatz || p.fach || p.compartment || p.storageLocation || '',
    });
  }
  return out;
}

/**
 * Ordnet den abgelaufenen/vorzubereitenden Paketen aus DPD Pickup (`TO_PREPARE` / `PREPARED_FOR_COLLECTION`)
 * das passende Regalfach aus PaketPilot zu (über Paketnummer oder Empfängername).
 */
export function rueckgabeMitFach(rueckgabeListe = [], packages = []) {
  const offene = (packages || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && !p.abgeholt && !p.pickedUp);
  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  return (rueckgabeListe || []).map((r) => {
    const code = dpdCodeAusTracking(r.code) || String(r.code || '').trim();
    const rName = norm(r.name);
    let treffer = offene.find((p) => {
      const pCode = dpdCodeAusTracking(p.trackingNumber || p.tracking || p.sendungsnummer || '');
      return code && pCode && pCode === code;
    });
    if (!treffer && rName) {
      treffer = offene.find((p) => {
        const pName = norm(p.name || p.empfaenger || `${p.vorname || ''} ${p.nachname || ''}`);
        return pName && pName === rName;
      });
    }
    return {
      ...r,
      fach: (treffer && (treffer.regalplatz || treffer.fach || treffer.compartment || treffer.storageLocation)) || '',
    };
  });
}

// ── Mehrsprachige Texte (DE / NL / EN) ───────────────────────────────────────

const TEXTE = {
  de: {
    titel: 'DPD Pickup-Anbindung',
    untertitel: 'Direkte Anbindung an DPD / Pickup (Namen vorab, Annahme, Ausgabe & Abgleich)',
    verbunden: 'Mit DPD Pickup verbunden',
    nichtVerbunden: 'Nicht mit DPD Pickup verbunden',
    pudoIdLabel: 'Pickup Shop-ID (z. B. NL24808)',
    codeAnfordern: 'Code per E-Mail anfordern',
    codeSenden: 'Sende E-Mail…',
    codeGesendet: 'Ein 6-stelliger Code wurde an die Shop-E-Mail von DPD gesendet.',
    codeLabel: '6-stelliger E-Mail-Code',
    codeBestaetigen: 'Code bestätigen & verbinden',
    verbinde: 'Verbinde…',
    neuPruefen: 'Neu prüfen',
    abmelden: 'Abmelden',
    aufGeraetAktiv: 'Auf diesem Gerät eingeschaltet',
    nurLesenHinweis: 'Im Nur-Lesen-Modus schlägt PaketPilot beim Scannen von DPD-Paketen automatisch den Empfängernamen aus Pickup nach, ohne in DPD zu buchen.',
    liveAnnahme: 'Annahme vom Fahrer live in DPD buchen',
    liveAusgabe: 'Ausgabe an Kunden live in DPD buchen',
    heuteTitel: 'Heute bei DPD Pickup',
    imShop: 'Pakete im Shop',
    unterwegs: 'Unterwegs / angekündigt',
    rueckgabe: 'Rückgabe an Fahrer',
    abgleichStarten: 'Bestand mit DPD Pickup abgleichen',
    abgleichLaeuft: 'Vergleiche Bestand…',
    protokollTitel: 'Protokoll',
    erfassen: 'DPD erfassen',
    erfassenTitel: 'DPD-Annahme (Fahrer-Lieferung)',
    liveBadge: 'LIVE — jedes gescannte Paket wird direkt bei DPD Pickup als angenommen (DELIVERY) gebucht',
    erfassenHinweis: 'Barcodes der DPD-Pakete nacheinander scannen (14-stellige Paketnummer oder 28-stelliger DPD-Leitcode). Empfängername und Regalfach werden sofort automatisch aus DPD Pickup zugeordnet.',
    vorLaeuft: 'Lade DPD-Vorab-Namen…',
    vorOk: 'DPD-Namen vorab bereit (0 ms Zuordnung)',
    vorLeer: 'Keine angekündigten DPD-Pakete gefunden',
    vorFehler: 'DPD-Vorab-Namen konnten nicht geladen werden',
    fach: 'Regalfach (optional, gilt für alle Scans)',
    fachAuto: 'Regalfach (leer = automatisch nach Nachname)',
    codeTippen: '14-stellige DPD-Paketnummer eingeben…',
    hinzufuegen: 'Buchen',
    wartet: 'Bucht bei DPD Pickup…',
    okDpd: 'Bei DPD Pickup gebucht & in PaketPilot angelegt',
    schon: 'Schon bei DPD Pickup im Shop eingebucht & in PaketPilot angelegt',
    ohneDpd: 'In PaketPilot angelegt — bei DPD Pickup nicht bestätigt',
    fremd: 'Nicht für diesen Shop bei DPD Pickup angekündigt',
    doppelt: 'Bereits in PaketPilot vorhanden',
    nochmal: 'Erneut bei DPD buchen',
    fertig: 'Fertig',
    fotosLaden: 'Fotos werden im Hintergrund gespeichert',
    fotosFehler: 'Foto(s) konnten nicht gespeichert werden',
    fotosOffen: 'Es werden noch Fotos im Hintergrund hochgeladen. Trotzdem schließen?',
    serverWarte: 'Warte auf Verbindung (Versuch)…',
    bannerRueck: 'DPD: Pakete zurück an den Fahrer',
    bannerLogin: 'DPD Pickup: Bitte Verbindung in den Einstellungen prüfen',
  },
  nl: {
    titel: 'DPD Pickup-koppeling',
    untertitel: 'Directe koppeling met DPD / Pickup (namen vooraf, inname, uitgifte & voorraadcontrole)',
    verbunden: 'Verbonden met DPD Pickup',
    nichtVerbunden: 'Niet verbonden met DPD Pickup',
    pudoIdLabel: 'Pickup Shop-ID (bijv. NL24808)',
    codeAnfordern: 'Code per e-mail aanvragen',
    codeSenden: 'E-mail versturen…',
    codeGesendet: 'Er is een 6-cijferige code naar het e-mailadres van de shop gestuurd.',
    codeLabel: '6-cijferige e-mailcode',
    codeBestaetigen: 'Code bevestigen & verbinden',
    verbinde: 'Verbinden…',
    neuPruefen: 'Opnieuw controleren',
    abmelden: 'Afmelden',
    aufGeraetAktiv: 'Ingeschakeld op dit apparaat',
    nurLesenHinweis: 'In alleen-lezen modus zoekt PaketPilot bij het scannen van DPD-pakketten automatisch de naam van de ontvanger op uit Pickup, zonder in DPD te boeken.',
    liveAnnahme: 'Inname van chauffeur direct in DPD boeken',
    liveAusgabe: 'Uitgifte aan klant direct in DPD boeken',
    heuteTitel: 'Vandaag bij DPD Pickup',
    imShop: 'Op voorraad in shop',
    unterwegs: 'Onderweg / aangekondigd',
    rueckgabe: 'Retour voor chauffeur',
    abgleichStarten: 'Voorraad vergelijken met DPD Pickup',
    abgleichLaeuft: 'Voorraad vergelijken…',
    protokollTitel: 'Logboek',
    erfassen: 'DPD ontvangen',
    erfassenTitel: 'DPD ontvangen (chauffeurslevering)',
    liveBadge: 'LIVE — elk gescand pakket wordt direct bij DPD Pickup als ontvangen (DELIVERY) geboekt',
    erfassenHinweis: 'Scan de barcodes van de DPD-pakketten achter elkaar (14-cijferig pakketnummer of 28-cijferige DPD-barcode). Naam van de ontvanger en vak worden direct automatisch uit DPD Pickup ingevuld.',
    vorLaeuft: 'DPD-namen vooraf laden…',
    vorOk: 'DPD-namen vooraf geladen (directe koppeling)',
    vorLeer: 'Geen aangekondigde DPD-pakketten gevonden',
    vorFehler: 'DPD-namen konden niet vooraf worden geladen',
    fach: 'Vak / plank (optioneel, geldt voor alle scans)',
    fachAuto: 'Vak / plank (leeg = automatisch op achternaam)',
    codeTippen: '14-cijferig DPD-pakketnummer invoeren…',
    hinzufuegen: 'Boeken',
    wartet: 'Boekt bij DPD Pickup…',
    okDpd: 'Bij DPD Pickup geboekt & in PaketPilot geregistreerd',
    schon: 'Al in DPD Pickup ingeboekt & in PaketPilot geregistreerd',
    ohneDpd: 'In PaketPilot geregistreerd — niet bevestigd bij DPD Pickup',
    fremd: 'Niet aangekondigd voor deze shop bij DPD Pickup',
    doppelt: 'Staat al in PaketPilot',
    nochmal: 'Opnieuw bij DPD boeken',
    fertig: 'Klaar',
    fotosLaden: 'Foto\'s worden op de achtergrond opgeslagen',
    fotosFehler: 'Foto(\'s) konden niet worden opgeslagen',
    fotosOffen: 'Er worden nog foto\'s geüpload. Toch sluiten?',
    serverWarte: 'Wachten op verbinding…',
    bannerRueck: 'DPD: pakketten terug naar de chauffeur',
    bannerLogin: 'DPD Pickup: controleer de verbinding in de instellingen',
  },
  en: {
    titel: 'DPD Pickup Integration',
    untertitel: 'Direct connection to DPD / Pickup (recipient names in advance, intake, pickup & stock check)',
    verbunden: 'Connected to DPD Pickup',
    nichtVerbunden: 'Not connected to DPD Pickup',
    pudoIdLabel: 'Pickup Shop ID (e.g. NL24808)',
    codeAnfordern: 'Request code by email',
    codeSenden: 'Sending email…',
    codeGesendet: 'A 6-digit code has been sent to the shop email registered with DPD.',
    codeLabel: '6-digit email code',
    codeBestaetigen: 'Verify code & connect',
    verbinde: 'Connecting…',
    neuPruefen: 'Refresh',
    abmelden: 'Disconnect',
    aufGeraetAktiv: 'Enabled on this device',
    nurLesenHinweis: 'In read-only mode, PaketPilot automatically looks up the recipient name from DPD Pickup when scanning a DPD barcode, without booking in DPD.',
    liveAnnahme: 'Book driver intake live in DPD',
    liveAusgabe: 'Book customer pickup live in DPD',
    heuteTitel: 'Today at DPD Pickup',
    imShop: 'In shop inventory',
    unterwegs: 'On the way / announced',
    rueckgabe: 'Return to driver',
    abgleichStarten: 'Compare inventory with DPD Pickup',
    abgleichLaeuft: 'Comparing inventory…',
    protokollTitel: 'Activity log',
    erfassen: 'Receive DPD',
    erfassenTitel: 'Receive DPD (driver delivery)',
    liveBadge: 'LIVE — every scanned parcel is booked directly as received (DELIVERY) in DPD Pickup',
    erfassenHinweis: 'Scan the DPD parcel barcodes one after another (14-digit parcel number or 28-digit DPD barcode). Recipient name and shelf are filled in automatically from DPD Pickup.',
    vorLaeuft: 'Preloading DPD names…',
    vorOk: 'DPD names preloaded (instant match)',
    vorLeer: 'No announced DPD parcels found',
    vorFehler: 'Could not preload DPD names',
    fach: 'Shelf (optional, applies to all scans)',
    fachAuto: 'Shelf (empty = automatic by last name)',
    codeTippen: 'Enter 14-digit DPD parcel number…',
    hinzufuegen: 'Book',
    wartet: 'Booking in DPD Pickup…',
    okDpd: 'Booked in DPD Pickup & registered in PaketPilot',
    schon: 'Already booked in DPD Pickup & registered in PaketPilot',
    ohneDpd: 'Registered in PaketPilot — not confirmed in DPD Pickup',
    fremd: 'Not announced for this shop in DPD Pickup',
    doppelt: 'Already in PaketPilot',
    nochmal: 'Retry booking in DPD',
    fertig: 'Done',
    fotosLaden: 'Saving photos in background',
    fotosFehler: 'Photo(s) could not be saved',
    fotosOffen: 'Photos are still uploading in the background. Close anyway?',
    serverWarte: 'Waiting for connection…',
    bannerRueck: 'DPD: parcels return to driver',
    bannerLogin: 'DPD Pickup: please check connection in settings',
  },
};

export function dpdT(lang = 'de') {
  const k = String(lang || 'de').slice(0, 2).toLowerCase();
  return TEXTE[k] || TEXTE.de;
}
